/**
 * Contato efetivo do lead e o que a conversão copia (puro).
 *
 * Depois que o lead vira cliente, o Cliente é a fonte do contato (CRM-11):
 * proposta e contrato já saem com o dado dele, então o cockpit fala com a
 * mesma pessoa. Espelha `crm.services.contato_do_lead` do backend.
 *
 * `dadosCopiadosNaConversao` lista exatamente o que
 * `crm.services.criar_cliente_de_lead` copia (CRM-03/41): o diálogo mostra
 * isso em vez de afirmar que "os dados já foram importados".
 */
import { formatarDataLocal } from "../../../utils/dinheiro";
import { mascaraDocumento } from "../../../utils/documento";
import { formatarEndereco } from "../../../utils/endereco";
import { enderecosDoLead } from "../enderecosLead";

export const PESSOA_FISICA = "PF";
export const PESSOA_JURIDICA = "PJ";

export const MSG_NOME_OBRIGATORIO = "Informe o nome completo do contato.";
export const MSG_TELEFONE_OU_EMAIL = "Informe pelo menos o telefone ou o e-mail.";
export const MSG_PJ_SEM_EMPRESA = "Para pessoa jurídica, informe a empresa no lead.";
export const AVISO_SEM_EMAIL =
  "Sem e-mail: a proposta e o contrato não poderão ser enviados por e-mail.";
export const AVISO_SEM_DOCUMENTO =
  "CPF/CNPJ: informe na ficha do cliente antes do contrato e da NF-e.";

/** {nome, email, telefone, fonte: "cliente" | "lead"} — sempre strings. */
export function contatoDoLead(lead) {
  const cliente = lead?.cliente_resumo;
  if (cliente && cliente.id) {
    return {
      nome: cliente.nome || lead.contato_nome || "",
      email: (cliente.email || "").trim(),
      telefone: (cliente.telefone || "").trim(),
      fonte: "cliente",
    };
  }
  return {
    nome: (lead?.contato_nome || "").trim(),
    email: (lead?.contato_email || "").trim(),
    telefone: (lead?.contato_telefone || "").trim(),
    fonte: "lead",
  };
}

/** PJ quando o lead tem empresa — a mesma inferência do arraste (CRM-14). */
export function tipoSugerido(lead) {
  return (lead?.empresa || "").trim() ? PESSOA_JURIDICA : PESSOA_FISICA;
}

/**
 * Erros por campo do formulário de contato do diálogo — {} quando pode
 * enviar. Mesma régua do backend (`criar_cliente_de_lead`).
 */
export function validarContatoParaConversao({ tipo, nome, telefone, email }, lead) {
  const erros = {};
  if (!String(nome || "").trim()) erros.contato_nome = MSG_NOME_OBRIGATORIO;
  if (!String(telefone || "").trim() && !String(email || "").trim()) {
    erros.contato_telefone = MSG_TELEFONE_OU_EMAIL;
  }
  if (tipo === PESSOA_JURIDICA && !(lead?.empresa || "").trim()) {
    erros.tipo = MSG_PJ_SEM_EMPRESA;
  }
  return erros;
}

function primeiroParceiro(lead) {
  const lista = Array.isArray(lead?.parceiros) ? lead.parceiros : [];
  const p = lista[0];
  return p ? p.representante_nome || p.nome || "" : "";
}

/**
 * Linhas {rotulo, valor} do que vai para o cliente, e `avisos` (strings)
 * do que falta. `contato` é o que está no formulário do diálogo.
 */
export function dadosCopiadosNaConversao(lead, { tipo, nome, telefone, email, documento }) {
  const pj = tipo === PESSOA_JURIDICA;
  const enderecos = enderecosDoLead(lead);
  const linhas = [];
  if (pj) {
    linhas.push({ rotulo: "Razão social", valor: (lead.empresa || "").trim() || "—" });
    linhas.push({ rotulo: "Pessoa de contato", valor: String(nome || "").trim() || "—" });
  } else {
    linhas.push({ rotulo: "Nome", valor: String(nome || "").trim() || "—" });
  }
  linhas.push({ rotulo: "Telefone", valor: String(telefone || "").trim() || "—" });
  linhas.push({ rotulo: "E-mail", valor: String(email || "").trim() || "—" });
  if (lead.telefone_secundario) {
    linhas.push({ rotulo: "Telefone 2", valor: lead.telefone_secundario });
  }
  if (!pj && lead.data_nascimento) {
    linhas.push({ rotulo: "Nascimento", valor: formatarDataLocal(lead.data_nascimento) });
  }
  if (lead.cargo) {
    linhas.push({ rotulo: pj ? "Cargo do contato" : "Profissão", valor: lead.cargo });
  }
  if (Array.isArray(lead.tags) && lead.tags.length) {
    linhas.push({ rotulo: "Tags", valor: lead.tags.join(", ") });
  }
  if (lead.observacoes) linhas.push({ rotulo: "Observações", valor: lead.observacoes });
  linhas.push({
    rotulo: enderecos.length > 1 ? `Endereços (${enderecos.length})` : "Endereço",
    valor: enderecos.length ? formatarEndereco(enderecos[0]) : "—",
  });
  const parceiro = primeiroParceiro(lead);
  if (parceiro) linhas.push({ rotulo: "Parceiro indicador", valor: parceiro });
  // Com cliente vinculado o documento e o da ficha; senao e o que esta no
  // dialogo (CRM-15), que vai ao lead e ao cliente novo.
  const docCliente = lead.cliente_resumo?.documento || "";
  const docDialogo = String(documento || "").trim();
  const documentoFinal = docCliente || docDialogo;
  linhas.push({
    rotulo: "CPF/CNPJ",
    valor: documentoFinal
      ? mascaraDocumento(documentoFinal, docCliente ? lead.cliente_resumo?.tipo : tipo)
      : "—",
  });

  const avisos = [];
  if (!String(email || "").trim()) avisos.push(AVISO_SEM_EMAIL);
  if (!documentoFinal) avisos.push(AVISO_SEM_DOCUMENTO);
  return { linhas, avisos };
}
